import {
  Box,
  Button,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { useCreateSampleCartRequest } from 'api/services/RequestContacts';
import { AxiosError } from 'axios';
import { WHITELABEL_EVENT_KEY } from 'components/constants/Constants';
import piwik from 'piwik/piwik';
import { useEffect, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  DEFAULT_CART_STATE,
  useCartState
} from '../../contexts/cart/CartContext';
import { CartState } from '../../contexts/cart/types';
import { CartTable } from './cartItemsSection/CartTable';
import { CartStateSynchronizer } from './CartStateSynchronizer';
import { ConfirmedContent } from './ConfirmedContent';
import { EmptyCart } from './EmptyCart';
import {
  ShippingDetailsSection,
  ShippingDetailsSectionStateType
} from './shippingDetailsSection/ShippingDetailsSection';
import { useShippingDetailsState } from './shippingDetailsSection/useShippingDetailsState';
import { createSampleCartPayload } from './createSampleCartPayload';

export const SampleCart: React.FC = () => {
  const { value, setValue } = useCartState();
  const emptyCart = !value.items.length;
  const methods = useForm<CartState>({ defaultValues: value });
  const [submittedConfirmed, setSubmittedConfirmed] = useState(false);
  const [, submitSampleCartRequest] = useCreateSampleCartRequest(
    WHITELABEL_EVENT_KEY ?? ''
  );

  const hasCustomShippingDetails = !!useWatch({
    control: methods.control,
    name: 'hasCustomShippingDetails'
  });

  const shippingDetaisSectionState = useShippingDetailsState(
    hasCustomShippingDetails
  );

  const submitCart = (data: CartState) => {
    submitSampleCartRequest({
      data: createSampleCartPayload(data, shippingDetaisSectionState)
    })
      .then(() => {
        toast.success('Sample cart submitted!');
        setSubmittedConfirmed(true);
        setValue(DEFAULT_CART_STATE);
      })

      .catch((error: AxiosError) => toast.error(error.message));
  };

  useEffect(() => {
    piwik.push(['trackPageView', `UXB cart checkout page`]);
  }, []);

  return (
    <FormProvider {...methods}>
      <CartStateSynchronizer />
      <Paper>
        {submittedConfirmed ? (
          <ConfirmedContent />
        ) : (
          <>
            <Typography variant="h4" sx={{ pt: 4, pb: 3, px: 4 }}>
              Sample Cart
            </Typography>
            <Divider />
            {emptyCart ? (
              <EmptyCart />
            ) : (
              <>
                <Stack sx={{ py: 3, px: 4 }} gap={3}>
                  <Box>
                    <Typography sx={{ mb: 1 }}>Products</Typography>
                    <CartTable />
                  </Box>
                  <Box>
                    <Typography sx={{ mb: 1 }}>Intention of request</Typography>
                    <TextField
                      placeholder="Add your message"
                      fullWidth
                      multiline
                      rows={4}
                      {...methods.register('message')}
                    />
                  </Box>
                  <Box>
                    <Typography sx={{ mb: 1 }}>Shipping details</Typography>
                    <ShippingDetailsSection
                      state={shippingDetaisSectionState}
                      switchToCustomDetails={() =>
                        methods.setValue('hasCustomShippingDetails', true)
                      }
                    />
                  </Box>
                </Stack>
                <Box
                  sx={theme => ({
                    bottom: 0,
                    position: 'sticky',
                    background: theme.palette.background.paper,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    p: 2,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 1,
                    zIndex: theme.zIndex.appBar
                  })}
                >
                  <Button variant="outlined">Cancel</Button>
                  <Button
                    disabled={
                      shippingDetaisSectionState.type ===
                      ShippingDetailsSectionStateType.LOADING
                    }
                    variant="contained"
                    onClick={methods.handleSubmit(submitCart)}
                  >
                    Send request
                  </Button>
                </Box>
              </>
            )}
          </>
        )}
      </Paper>
    </FormProvider>
  );
};
